import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Pagination,
  Search,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import { CourseUserDeadline } from 'components/courses/common';
import { PathStepParticipantsEdit, PathStepProgressStatus } from '.';

import type { GetPathParticipantsRes } from 'shared-types/responses';

export type UpdatePathCourseParticipantObject = {
  page?: number;
  search?: string;
  showLoading?: boolean;
};

interface Props {
  stepId: number;
}

const PathStepParticipants = ({ stepId }: Props) => {
  const {
    messages,
    pageState: { path }
  } = useReduxState();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, loading, updateData } = useApiData<GetPathParticipantsRes>({
    url:
      `/admin/paths/${path.id}/participants?pathAccess=current` +
      `&stepId=${stepId}` +
      `&page=${page}` +
      `&search=${search}`
  });

  return (
    <div className="participants">
      <Box
        headerContent={
          <div className="filters">
            <div className="filters-search">
              <Search
                onSearch={({ search }) => setSearch(search)}
                placeholder={messages.usersSearchUsers}
              />
            </div>
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.commonUsers}
      >
        {data?.users?.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUser}</BoxTableCell>
              <BoxTableCell>{messages.commonAdded}</BoxTableCell>
              <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
              <BoxTableCell>{messages.commonProgress}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {data?.users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User
                    enableProfileLink={true}
                    linkAs={`/admin/paths/${path.id}/users/${user.id}`}
                    linkHref="/admin/paths/[pathId]/users/[userId]"
                    user={user}
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonAdded}
                >
                  <Icon
                    icon={['far', 'calendar-alt']}
                    style={{
                      display: 'inline-block',
                      fontSize: '1.3rem',
                      marginRight: 5,
                      position: 'relative',
                      top: -1
                    }}
                  />
                  <DisplayFormattedDate date={user.addedToPath} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.courseTargetSchedule}
                >
                  <CourseUserDeadline
                    deadline={user.deadlines[stepId]}
                    isEdited={Boolean(user.userSettings[stepId]?.deadlineType)}
                  />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonProgress}
                >
                  <PathStepProgressStatus
                    id={`${stepId}-${user.id}`}
                    items={data.steps[0].items}
                    pathCompletion={user.progressData.pathCompletion}
                    progressData={user.progressData.steps[stepId]}
                    progressDataByItem={
                      user.progressData.steps[stepId].itemProgressByTypeId
                    }
                  />
                </BoxTableCell>

                <BoxTableCell shrink={true}>
                  <MoreOptions>
                    <PathStepParticipantsEdit
                      step={data.steps[0]}
                      userSettings={user.userSettings[stepId]}
                      targetUser={user}
                      updateUsers={updateData}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />
    </div>
  );
};

export default PathStepParticipants;
